import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'
import { getDB } from '../utils/indexedDb'
import { encryptWithFamilyKey, decryptWithFamilyKey } from '../utils/keyManagement'
import { useAuthStore } from './authStore'
import { getOrPromptForPassword } from '../utils/passwordHandling'
import { format } from 'date-fns'
import logger from '../utils/logger'

type CalendarEntry = Database['public']['Tables']['calendar_entries']['Row']
type Child = Database['public']['Tables']['children']['Row']

interface CalendarState {
  entries: CalendarEntry[]
  children: Child[]
  selectedChildId: string | null
  selectedHome: "home_a" | "home_b" | null
  currentDate: string
  viewMode: 'month' | 'week'
  isLoading: boolean
  error: string | null
  lastFetch: Record<string, number> // Timestamp ostatniego pobrania danych dla danego dziecka i zakresu dat
  fetchEpoch?: Record<string, number>
  
  // Akcje
  fetchEntries: (childId: string, startDate: string, endDate: string) => Promise<void>
  fetchChildren: (familyId: string) => Promise<void>
  addEntry: (entry: Omit<CalendarEntry, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateEntry: (id: string, updates: Partial<CalendarEntry>) => Promise<void>
  deleteEntry: (id: string) => Promise<void>
  setSelectedChild: (childId: string) => void
  setSelectedHome: (home: "home_a" | "home_b" | null) => void
  setCurrentDate: (date: string) => void
  setViewMode: (mode: 'month' | 'week') => void
  clearCache: () => void
}

export const useCalendarStore = create<CalendarState>()(
  persist(
    (set, get) => ({
      entries: [],
      children: [],
      selectedChildId: null,
      selectedHome: null,
      currentDate: format(new Date(), 'yyyy-MM-dd'),
      viewMode: 'month',
      isLoading: false,
      error: null,
      lastFetch: {},
      fetchEpoch: {},
      
      fetchEntries: async (childId, startDate, endDate) => {
        // Sprawdź, czy dane zostały już pobrane niedawno (krótkie okno dla szybkiej nawigacji)
        const fetchKey = `${childId}-${startDate}-${endDate}`
        const lastFetchTime = get().lastFetch[fetchKey] || 0
        const now = Date.now()
        
        if (now - lastFetchTime < 800) {
          logger.debug(`🔄 Pomijam ponowne pobranie danych dla ${fetchKey}, ostatnie pobranie: ${new Date(lastFetchTime).toLocaleTimeString()}`)
          return
        }
        
        const epoch = Date.now()
        set((state) => ({ 
          ...state, 
          isLoading: true, 
          error: null,
          fetchEpoch: { ...(state.fetchEpoch || {}), [fetchKey]: epoch }
        }))
        logger.debug(`🔍 Pobieranie wpisów dla dziecka ${childId} od ${startDate} do ${endDate}`)
        
        try {
          // Dla tymczasowego dziecka zwracamy puste dane
          if (childId === 'temp-child-id') {
            logger.debug('👶 Używam tymczasowego dziecka, zwracam puste wpisy')
            // Zastosuj wynik tylko jeśli to wciąż aktualne żądanie
            if ((get().fetchEpoch || {})[fetchKey] === epoch) {
              set((state) => ({ 
                ...state,
                entries: [],
                isLoading: false,
                lastFetch: { ...state.lastFetch, [fetchKey]: now }
              }))
            } else {
              logger.debug('⏭️ Zignorowano przestarzałą odpowiedź (temp child) dla', fetchKey)
            }
            return
          }
          
          const { data, error } = await supabase
            .from('calendar_entries')
            .select('*')
            .eq('child_id', childId)
            .gte('date', startDate)
            .lte('date', endDate)
            .order('date')
          
          if (error) throw error
          
          logger.debug(`✅ Pobrano ${data?.length || 0} wpisów kalendarza`)
          
          // Przetwarzanie zaszyfrowanych komentarzy
          const processedEntries = await Promise.all((data || []).map(async (entry: any) => {
            // Jeśli komentarz jest zaszyfrowany, próbujemy go odszyfrować
            if (entry.comment && entry.is_encrypted) {
              try {
                logger.debug(`🔓 Deszyfrowanie komentarza dla wpisu ${entry.id}...`)
                
                // Pobierz aktualnego użytkownika
                const { user } = useAuthStore.getState()
                if (!user) {
                  throw new Error('Brak zalogowanego użytkownika')
                }
                
                // Pobierz hasło z sesji lub poproś użytkownika
                const password = await getOrPromptForPassword(user.id, 'Podaj hasło do odszyfrowania komentarza')
                if (!password) {
                  throw new Error('Nie podano hasła do odszyfrowania')
                }
                
                // Deszyfrowanie komentarza
                const encryptedComment = JSON.parse(entry.comment)
                const decryptedComment = await decryptWithFamilyKey(encryptedComment, entry.family_id, password)
                
                if (decryptedComment) {
                    logger.debug('✅ Komentarz odszyfrowany')
                  return { ...entry, comment: decryptedComment, is_encrypted: false }
                } else {
                  logger.warn(`⚠️ Nie udało się odszyfrować komentarza dla wpisu ${entry.id}`)
                }
              } catch (decryptError) {
                logger.error(`❌ Błąd deszyfrowania komentarza dla wpisu ${entry.id}:`, decryptError)
                // Kontynuuj z zaszyfrowanym komentarzem
              }
            }
            return entry
          }))
          
          // Zastosuj wynik tylko jeśli to wciąż aktualne żądanie
          if ((get().fetchEpoch || {})[fetchKey] === epoch) {
            // Zachowaj optymistyczne/wyjściowe wpisy (offline/optimistic), aby nie zgubić pierwszego kliknięcia,
            // gdy odpowiedź fetch przyjdzie chwilę po dodaniu
            const currentEntries = get().entries
            const optimisticEntries = (currentEntries || []).filter((e: any) => typeof e.id === 'string' && (e.id.startsWith('optimistic-') || e.id.startsWith('offline-')))
            const merged = [
              ...(processedEntries as CalendarEntry[]),
              ...optimisticEntries.filter(opt => !(processedEntries as CalendarEntry[]).some(fe => fe.id === (opt as any).id))
            ]
            set((state) => ({ 
              ...state,
              entries: merged,
              isLoading: false,
              lastFetch: { ...state.lastFetch, [fetchKey]: now }
            }))
          } else {
            logger.debug('⏭️ Zignorowano przestarzałą odpowiedź dla', fetchKey)
          }
        } catch (error: any) {
          logger.error('❌ Błąd pobierania wpisów:', error)
          if ((get().fetchEpoch || {})[fetchKey] === epoch) {
            set((state) => ({ 
              ...state,
              error: error.message || 'Błąd pobierania wpisów kalendarza',
              isLoading: false,
            }))
          }
        }
      },
      
      fetchChildren: async (familyId) => {
        // Sprawdź, czy dane zostały już pobrane niedawno (krótkie okno)
        const fetchKey = `children-${familyId}`
        const lastFetchTime = get().lastFetch[fetchKey] || 0
        const now = Date.now()
        
        if (now - lastFetchTime < 1500) {
          logger.debug(`🔄 Pomijam ponowne pobranie dzieci dla ${familyId}, ostatnie pobranie: ${new Date(lastFetchTime).toLocaleTimeString()}`)
          return
        }
        
        const epoch = Date.now()
        set((state) => ({ 
          ...state, 
          isLoading: true, 
          error: null,
          fetchEpoch: { ...(state.fetchEpoch || {}), [fetchKey]: epoch }
        }))
        logger.debug(`👨‍👩‍👧‍👦 Pobieranie dzieci dla rodziny ${familyId}`)
        
        try {
          // Dla tymczasowej rodziny, zwracamy puste dane
          if (familyId === 'temporary-family-id') {
            logger.debug('👪 Używam tymczasowej rodziny, zwracam puste dane')
            if ((get().fetchEpoch || {})[fetchKey] === epoch) {
              set((state) => ({ 
                ...state,
                children: [],
                isLoading: false,
                lastFetch: { ...state.lastFetch, [fetchKey]: now }
              }))
            }
            return
          }
          
          const { data, error } = await supabase
            .from('children')
            .select('*')
            .eq('family_id', familyId)
            .order('created_at')
          
          if (error) throw error
          
          const children = data as Child[]
          logger.debug(`✅ Pobrano ${children.length} dzieci`)
          
          // Sprawdź, czy wybrane dziecko nadal istnieje
          const currentState = get()
          let newSelectedChildId = currentState.selectedChildId
          logger.debug(`🔍 Stan przed wyborem dziecka:`, {
            currentSelectedChildId: currentState.selectedChildId,
            childrenCount: children.length,
            childrenIds: children.map(c => c.id)
          })
          
          // Jeśli nie ma wybranego dziecka lub wybrane dziecko to temp-child-id, a mamy prawdziwe dzieci
          if ((!currentState.selectedChildId || currentState.selectedChildId === 'temp-child-id') && children.length > 0) {
            newSelectedChildId = children[0].id
            logger.debug(`🔄 Auto-wybór pierwszego dziecka: ${newSelectedChildId}`)
          }
          // Sprawdź, czy wybrane dziecko nadal istnieje (dla istniejących wyborów)
          else if (currentState.selectedChildId && currentState.selectedChildId !== 'temp-child-id') {
            const selectedChildExists = children.some(child => child.id === currentState.selectedChildId)
            if (!selectedChildExists) {
              logger.warn(`⚠️ Wybrane dziecko ${currentState.selectedChildId} nie istnieje, resetuję wybór`)
              // Jeśli nie ma dzieci, ustaw tymczasowe dziecko
              if (children.length === 0) {
                newSelectedChildId = 'temp-child-id'
              } else {
                // W przeciwnym razie wybierz pierwsze dziecko
                newSelectedChildId = children[0].id
              }
              logger.debug(`🔄 Nowe wybrane dziecko: ${newSelectedChildId}`)
            }
          }
          
          if ((get().fetchEpoch || {})[fetchKey] === epoch) {
            set((state) => ({ 
              ...state,
              children,
              selectedChildId: newSelectedChildId,
              isLoading: false,
              lastFetch: { ...state.lastFetch, [fetchKey]: now }
            }))
          }
        } catch (error: any) {
          logger.error('❌ Błąd pobierania dzieci:', error)
          if ((get().fetchEpoch || {})[fetchKey] === epoch) {
            set((state) => ({ 
              ...state,
              error: error.message || 'Błąd pobierania dzieci',
              isLoading: false,
            }))
          }
        }
      },
      
      addEntry: async (entry) => {
    // Nie ustawiamy globalnego isLoading – unikamy migania UI, używamy optymistycznej aktualizacji
    set((state) => ({ ...state, error: null }))
    logger.debug('➕ Dodawanie nowego wpisu:', entry)
    
    try {
      // Dla tymczasowego dziecka, tylko symulujemy dodanie wpisu
      if (entry.child_id === 'temp-child-id' || entry.family_id === 'temporary-family-id') {
        logger.debug('👶 Używam tymczasowego dziecka/rodziny, symulacja dodania wpisu')
        
        // Generuj tymczasowe ID
        const tempEntry = {
          id: `temp-${Date.now()}`,
          ...entry,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as CalendarEntry
        
        set((state) => ({ 
          ...state,
          entries: [...state.entries, tempEntry],
        }))
        return
      }
      
      // Przygotuj wpis z ewentualnie zaszyfrowanym komentarzem
      let processedEntry = { ...entry };
      
      // Jeśli jest komentarz, szyfrujemy go
      if (entry.comment) {
        try {
          logger.debug('🔐 Szyfrowanie komentarza...')
          
          // Pobierz aktualnego użytkownika
          const { user } = useAuthStore.getState()
          if (!user) {
            throw new Error('Brak zalogowanego użytkownika')
          }
          
          // Pobierz hasło z sesji lub poproś użytkownika
          const password = await getOrPromptForPassword(user.id, 'Podaj hasło do zaszyfrowania komentarza')
          if (!password) {
            throw new Error('Nie podano hasła do szyfrowania')
          }
          
          // Szyfrowanie komentarza
          const encryptedComment = await encryptWithFamilyKey(entry.comment, entry.family_id, password)
          if (encryptedComment) {
            processedEntry.comment = JSON.stringify(encryptedComment)
            processedEntry.is_encrypted = true
            logger.debug('✅ Komentarz zaszyfrowany')
          } else {
            logger.warn('⚠️ Nie udało się zaszyfrować komentarza - używam nieszyfrowanego')
          }
        } catch (encryptError) {
          logger.error('❌ Błąd szyfrowania komentarza:', encryptError)
          // Kontynuuj bez szyfrowania
        }
      }
      
      // Sprawdź połączenie z internetem
      if (!navigator.onLine) {
        logger.warn('🔌 Brak połączenia z internetem, zapisuję lokalnie')
        
        // Generuj tymczasowe ID, które zostanie zastąpione po synchronizacji
        const offlineEntry = {
          id: `offline-${Date.now()}`,
          ...processedEntry,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          sync_status: 'pending' as const
        }
        
        // Zapisz do IndexedDB
        const db = await getDB()
        await db.add('calendar_entries', offlineEntry)
        
        // Dodaj do pending_changes
        await db.add('pending_changes', {
          id: `change-${Date.now()}`,
          table: 'calendar_entries',
          action: 'create',
          data: offlineEntry,
          timestamp: Date.now()
        })
        
        // Dodaj do lokalnego stanu
        set((state) => ({ 
          ...state,
          entries: [...state.entries, offlineEntry as unknown as CalendarEntry],
          isLoading: false,
        }))
        
        // Zarejestruj zadanie synchronizacji
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.ready
            // @ts-ignore - Background Sync API może nie być rozpoznawane przez TypeScript
            if (registration.sync) {
              // @ts-ignore
              await registration.sync.register('sync-calendar-entries')
              logger.debug('🔄 Zarejestrowano zadanie synchronizacji')
            } else {
              logger.warn('⚠️ Background Sync API nie jest dostępne')
            }
          } catch (err) {
            logger.error('❌ Błąd rejestracji zadania synchronizacji:', err)
          }
        }
        
        return
      }
      
      // Optymistycznie dodaj wpis lokalnie, a potem zapisz do Supabase i podmień ID
      const optimisticId = `optimistic-${Date.now()}`
      const optimisticEntry: CalendarEntry = {
        id: optimisticId as any,
        ...(processedEntry as any),
        created_at: new Date().toISOString() as any,
        updated_at: new Date().toISOString() as any,
      }
      set((state) => ({ 
        ...state,
        entries: [...state.entries, optimisticEntry],
      }))

      try {
        const { data, error } = await supabase
          .from('calendar_entries')
          .insert(processedEntry)
          .select()
        
        if (error) throw error
        logger.debug('✅ Wpis dodany pomyślnie')

        // Zapisz również do IndexedDB dla dostępu offline
        try {
          const db = await getDB()
          await db.put('calendar_entries', {
            ...data[0],
            sync_status: 'synced'
          })
          logger.debug('💾 Wpis zapisany również lokalnie')
        } catch (dbError) {
          logger.warn('⚠️ Błąd zapisywania do IndexedDB:', dbError)
        }

        // Podmień wpis optymistyczny na realny z bazy – jeśli fetchEntries już nadpisał stan,
        // realny wpis i tak trafi przez merge przy fetchu; tutaj próbujemy zmapować optymistyczny
        set((state) => ({ 
          ...state,
          entries: state.entries.map(e => e.id === optimisticId ? (data[0] as CalendarEntry) : e),
        }))
      } catch (error) {
        logger.error('❌ Błąd dodawania wpisu (online):', error)
        // Jeśli błąd to duplikat (23505), zostaw optymistyczny wpis – fetchEntries go zaraz nadpisze realnym
        const pgErr = error as any
        const isDuplicate = pgErr?.code === '23505'
        set((state) => ({ 
          ...state,
          entries: isDuplicate ? state.entries : state.entries.filter(e => e.id !== optimisticId),
          error: isDuplicate ? state.error : ((error as any)?.message || 'Błąd dodawania wpisu kalendarza'),
        }))
      }
    } catch (error: any) {
      logger.error('❌ Błąd dodawania wpisu:', error)
      set((state) => ({ 
        ...state,
        error: error.message || 'Błąd dodawania wpisu kalendarza',
      }))
    }
  },
      
      updateEntry: async (id, updates) => {
        // Unikamy globalnego isLoading – optymistyczna aktualizacja
        set((state) => ({ ...state, error: null }))
        logger.debug('✏️ Aktualizacja wpisu:', id, updates)
        
        try {
          // Dla tymczasowych wpisów, tylko symulujemy aktualizację
          if (id.startsWith('temp-')) {
            logger.debug('👶 Aktualizacja tymczasowego wpisu')
            
            set((state) => ({ 
              ...state,
              entries: state.entries.map(entry => 
                entry.id === id ? { ...entry, ...updates, updated_at: new Date().toISOString() } : entry
              ),
            }))
            return
          }
          // Optymistycznie zaktualizuj wpis lokalnie
          let previousEntry: CalendarEntry | null = null
          set((state) => ({
            ...state,
            entries: state.entries.map(entry => {
              if (entry.id === id) {
                previousEntry = { ...entry }
                return { ...entry, ...updates, updated_at: new Date().toISOString() as any }
              }
              return entry
            })
          }))

          try {
            const { error } = await supabase
              .from('calendar_entries')
              .update(updates)
              .eq('id', id)
            if (error) throw error
            logger.debug('✅ Wpis zaktualizowany pomyślnie')
          } catch (err) {
            logger.error('❌ Błąd aktualizacji wpisu (online):', err)
            // Revert optimistic change
            if (previousEntry) {
              set((state) => ({
                ...state,
                entries: state.entries.map(entry => entry.id === id ? previousEntry as CalendarEntry : entry),
                error: (err as any)?.message || 'Błąd aktualizacji wpisu kalendarza'
              }))
            }
          }
        } catch (error: any) {
          logger.error('❌ Błąd aktualizacji wpisu:', error)
          set((state) => ({ 
            ...state,
            error: error.message || 'Błąd aktualizacji wpisu kalendarza',
          }))
        }
      },
      
      deleteEntry: async (id) => {
        // Unikamy globalnego isLoading – optymistyczne usuwanie
        set((state) => ({ ...state, error: null }))
        logger.debug('🗑️ Usuwanie wpisu:', id)
        
        try {
          // Dla tymczasowych wpisów, tylko symulujemy usunięcie
          if (id.startsWith('temp-')) {
            logger.debug('👶 Usuwanie tymczasowego wpisu')
            
            set((state) => ({ 
              ...state,
              entries: state.entries.filter(entry => entry.id !== id),
            }))
            return
          }
          // Optymistycznie usuń wpis lokalnie, z możliwością revertu
          let removedEntry: CalendarEntry | null = null
          set((state) => ({ 
            ...state,
            entries: state.entries.filter(entry => {
              if (entry.id === id) {
                removedEntry = entry
                return false
              }
              return true
            })
          }))

          try {
            const { error } = await supabase
              .from('calendar_entries')
              .delete()
              .eq('id', id)
            if (error) throw error
            logger.debug('✅ Wpis usunięty pomyślnie')
          } catch (err) {
            logger.error('❌ Błąd usuwania wpisu (online):', err)
            // Revert optimistic removal
            if (removedEntry) {
              set((state) => ({ 
                ...state,
                entries: [...state.entries, removedEntry as CalendarEntry],
                error: (err as any)?.message || 'Błąd usuwania wpisu kalendarza',
              }))
            }
          }
        } catch (error: any) {
          logger.error('❌ Błąd usuwania wpisu:', error)
          set((state) => ({ 
            ...state,
            error: error.message || 'Błąd usuwania wpisu kalendarza',
          }))
        }
      },
      
      setSelectedChild: (childId) => {
        logger.debug('👶 Wybrano dziecko:', childId)
        // Wyczyść wpisy przy zmianie dziecka, aby uniknąć mieszania danych
        set((state) => ({ 
          ...state,
          selectedChildId: childId,
          entries: [],
          lastFetch: {}
        }))
      },
      
      setSelectedHome: (home) => {
        logger.debug('🏠 Wybrano dom:', home)
        set((state) => ({ ...state, selectedHome: home }))
      },
      
      setCurrentDate: (date) => {
        logger.debug('📅 Zmieniono datę na:', date)
        set((state) => ({ ...state, currentDate: date }))
      },
      
      setViewMode: (mode) => {
        logger.debug('👁️ Zmieniono tryb widoku na:', mode)
        set((state) => ({ ...state, viewMode: mode }))
      },
      
      clearCache: () => {
        logger.debug('🗑️ Czyszczenie cache kalendarza')
        set((state) => ({ 
          ...state,
          entries: [],
          lastFetch: {},
          selectedChildId: null,
          selectedHome: null,
        }))
      },
    }),
    {
      name: '2domy-calendar-storage',
      partialize: (state) => ({ 
        selectedChildId: state.selectedChildId,
        selectedHome: state.selectedHome,
        viewMode: state.viewMode,
      }),
    }
  )
) 